import { useCallback, useReducer } from "react";
import React, { useEffect } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { useParams, Link } from "react-router-dom";

import ai from "../../helpers/axios";
import { isTripClosedForUser } from "../../helpers/is-trip-editable";

import StateDisplay from "../reusable-modules/state-display";

import Loader from "../loader";
import PageHeader from "../shared/page-header";
import { LoadingButton } from "../shared/loading-button";
import { toast } from "react-toastify";

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "beginDataDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "setHTML": {
      return {
        ...state,
        loading: false,
        invoiceHtml: action.payload.html,
      };
    }

    case "beginPDFDownloading": {
      return {
        ...state,
        loadingPDF: true,
      };
    }

    case "endPDFDownloading": {
      return {
        ...state,
        loadingPDF: false,
      };
    }

    default:
      break;
  }
  return state;
};

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const InvoicePreviewContent = (props) => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const { tripId, invoiceId } = useParams();

  const userRoles = (user && user["https://new.evojets.com/roles"]) || [];
  const superAdmin = userRoles.some((role) => role === "Super Admin");
  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;

  let initialState = {
    loading: true,
    tripTag: "",
    tripVerified: false,
    tripLocked: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const readOnly = isTripClosedForUser({
    verified: state.tripVerified,
    locked: state.tripLocked,
    superAdmin,
    verificationAdmin,
  });

  const getData = async () => {
    dispatch({ type: "beginDataDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const html = await ai
      .auth(token)
      .get(`/api/invoices/preview/${invoiceId}`)
      .catch((error) => {
        console.error(error);
      });

    const { data: trip } = await ai
      .auth(token)
      .get(`api/trips/${tripId}`)
      .catch((error) => {
      console.error(error);
    });

    dispatch({
      type: "fieldChanged",
      field: "tripTag",
      value: trip.tripTag,
    });

    dispatch({
      type: "fieldChanged",
      field: "tripVerified",
      value: !!trip.verified,
    });

    dispatch({
      type: "fieldChanged",
      field: "tripLocked",
      value: !!trip.lockState?.locked,
    });

    if (html) {
      dispatch({
        type: "setHTML",
        payload: {
          html: html.data.html,
        },
      });

      dispatch({
        type: "fieldChanged",
        field: "fileName",
        value: html.data?.templateData?.fileName,
      });
    }
  };

  const handlePDF = async () => {
    dispatch({ type: "beginPDFDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const pdf = await ai
      .auth(token)
      .get(`/api/invoices/preview/${invoiceId}/pdf`, {
        responseType: "arraybuffer",
      })
      .catch((error) => {
        console.error(error);
      });

    if (pdf) {
      const saveFile = async () => {
        try {
          const url = window.URL.createObjectURL(
            new Blob([pdf.data], { type: "application/pdf" })
          );

          let link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `${state.fileName ? state.fileName : `evo-invoice.pdf`}`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 1000);
        } catch (err) {
          console.error("Error saving file:", err);
        }
      };

      // Call the saveFile function
      saveFile();
    }

    dispatch({ type: "endPDFDownloading" });
  };

  useEffect(() => {
    getData();
    document.title = "Invoice Preview | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <PageHeader
        pageTitle="Preview Invoice"
        breadcrumbs={[
          {link: '/trips', label: 'Trips'},
          {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
          {
            label: `Preview Invoice`,
            isCurrentPage: true,
          },
        ]}
        goBackLink={`/trips/${tripId}`}
      />

      {state.loading && <Loader></Loader>}

      {!state.loading && (
        <React.Fragment>
          <h4>Shortcuts</h4>

          <div className="d-flex flex-wrap flex-align-center column-gap-20">
            <Link to={`/trips/${tripId}`} className={`base-button evo-blue`}>
              Return to Trip
            </Link>

            {!readOnly && (
              <Link
                to={`/trips/${tripId}/invoices/${invoiceId}`}
                className={`base-button evo-blue`}>
                Edit Invoice
              </Link>
            )}

            <button
              className="evo-blue base-button "
              onClick={handlePDF}
              disabled={state.loadingPDF}>
              {state.loadingPDF ? "Generating PDF..." : "Save as PDF"}
            </button>
          </div>

          <hr />
        </React.Fragment>
      )}

      <iframe
        title="Invoice Preview"
        className="contract-preview-container"
        id="contractPreviewIframe"
        sandbox="allow-same-origin"
        srcDoc={state.invoiceHtml}></iframe>

      <StateDisplay data={state} user={user}></StateDisplay>
    </React.Fragment>
  );
};

export default InvoicePreviewContent;
